import { allergenName, conflictLevel, conflictsFor, worthStopping, type Item, type ProfileAllergen } from '@shared/types'

/**
 * How loudly a conflict should speak.
 *
 * 'severe' outranks everything: it is the one that ends in an ambulance.
 * 'rule' comes next, because a school rule binds even when the food is
 * harmless to the child -- the lunch comes back uneaten, or goes in the bin.
 * 'avoid' is an intolerance or a preference and stays quiet, because a red
 * warning on every mild dislike teaches people to ignore red warnings.
 */
export type Level = 'severe' | 'rule' | 'avoid'

export interface Conflict {
  level: Level
  /** Every row this food ran into, child allergens and school rules alike. */
  rows: ProfileAllergen[]
}

/**
 * The one sentence that has to appear wherever a conflict does.
 *
 * Five Lunches cannot read a label, cannot know what a manufacturer changed
 * last month, and only knows the tags a person typed. A clean result means
 * "nothing we were told about" and never "safe", so nothing in the interface
 * may imply otherwise.
 */
export const CHECK_THE_PACKET =
  'Five Lunches only knows the tags you have given it. Check the packet every time.'

/**
 * Severe first, then a binding school rule, then everything else.
 *
 * The rule itself lives in shared/types, because the server applies it too
 * when a lunch is copied to a sibling, and two copies of it would drift.
 */
const levelOf = conflictLevel

/**
 * What a food runs into, or null when it runs into nothing we were told
 * about. Null is not a safety claim -- see CHECK_THE_PACKET.
 */
export function conflictOf(
  item: { allergens: Item['allergens'] } | undefined,
  against: ProfileAllergen[],
): Conflict | null {
  if (!item || against.length === 0) return null
  const rows = conflictsFor(item, against)
  return rows.length === 0 ? null : { level: levelOf(rows), rows }
}

/** "peanut", "peanut and milk", "peanut, milk and sesame". */
function listOf(names: string[]): string {
  const seen = [...new Set(names)]
  if (seen.length <= 1) return seen[0] ?? ''
  return `${seen.slice(0, -1).join(', ')} and ${seen[seen.length - 1]}`
}

/**
 * The conflict in the family's own terms, naming the child rather than
 * talking about "the profile".
 *
 * Grouped by what it means rather than listed flat, because the three things
 * are not the same and a parent acts on them differently. One food can be all
 * three at once: severely allergic to the peanut in it, mildly avoiding the
 * milk, and banned from the classroom for the tree nuts.
 */
export function conflictLine(c: Conflict, childName: string): string {
  const who = childName.trim() || 'your child'
  // Known allergens read better lowercase mid-sentence; an 'other' row is the
  // parent's own wording and keeps whatever case they typed.
  const nameOf = (r: ProfileAllergen) =>
    r.slug === 'other' ? allergenName(r) : allergenName(r).toLowerCase()
  const part = (rows: ProfileAllergen[], say: (names: string) => string) =>
    rows.length > 0 ? say(listOf(rows.map(nameOf))) : null

  /*
   * A diet says its own name rather than the ingredient that matched: the
   * useful sentence is "not vegetarian", not "contains gelatin". Which
   * ingredient tripped it is on the food itself.
   */
  return [
    part(c.rows.filter((r) => r.kind === 'child' && r.severity === 'severe'), (n) => `Severe allergy — ${n}`),
    part(c.rows.filter((r) => r.kind === 'child' && r.severity !== 'severe'), (n) => `${who} avoids ${n}`),
    part(c.rows.filter((r) => r.kind === 'diet'), (n) => `Not ${n}`),
    part(c.rows.filter((r) => r.kind === 'school'), (n) => `Not allowed at school — ${n}`),
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The glyph a conflict wears, in text rather than emoji.
 *
 * An emoji carries its own colour and cannot take the one around it, so a red
 * 🚫 would shout at the same volume as a severe allergy. These inherit, and
 * the three voices stay unequal.
 */
export const conflictMark = (c: Conflict): string => (c.level === 'severe' ? '⚠' : '⊘')

/** The short form, for a tile's tooltip and its screen-reader label. */
export const conflictShort = (c: Conflict): string =>
  `${listOf(c.rows.map(allergenName))} — ${
    c.level === 'severe'
      ? 'severe allergy'
      : c.level === 'rule'
        ? c.rows.some((r) => r.kind === 'diet')
          ? 'against a restriction you set'
          : 'not allowed at school'
        : 'one to avoid'
  }`

/**
 * Whether placing this food should stop and ask.
 *
 * Severe allergies and school rules do; an intolerance does not. A dialog in
 * front of every mildly disliked food is a dialog people learn to dismiss
 * without reading, which costs exactly the attention the severe case needs.
 * The quiet ones still carry a flag on the tile.
 */
export const worthAsking = (c: Conflict | null): boolean => c !== null && worthStopping(c.rows)
